import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Switch, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { apiBlobUrl } from '@/data/api';
import { getLegalConfig, saveLegalConfig, legalDocPath, pushLegalPages, type LegalConfig, type LegalDoc } from '@/data/client';
import type { AdminClient, DirtyReporter } from './types';


/* Publish-ready Privacy Policy / Terms of Use / point-of-collection notices for a
   client's website, from a per-client form — so any team member can produce them
   without a command line. The form is the only thing edited per client: every
   site-specific decision is a flag, and the legal wording lives in
   worker/legal-templates.ts. Preview and download both render from the SAVED
   config, so what you see is exactly what you hand to the client. */
export function LegalPagesPanel({ client, onDirtyChange }: { client: AdminClient; onDirtyChange?: DirtyReporter }) {
  const qc = useQueryClient();
  const state = useQuery({ queryKey: ['admin', 'legal', client.orgId], queryFn: () => getLegalConfig(client.orgId) });

  const [cfg, setCfg] = useState<LegalConfig | null>(null);
  const [addressText, setAddressText] = useState('');
  const [saved, setSaved] = useState<string>(''); // JSON snapshot of the last saved config, for the dirty check
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const [tab, setTab] = useState<LegalDoc>('privacy');
  const [fragment, setFragment] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [noticesText, setNoticesText] = useState('');
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const urlRef = useRef<string | null>(null);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState<{ repo: string; branch: string; files: { path: string; url: string }[] } | null>(null);
  const [pushErr, setPushErr] = useState<string | null>(null);

  // Seed the form once the stored (or default) config arrives.
  useEffect(() => {
    if (!state.data || cfg) return;
    setCfg(state.data.config);
    setAddressText(state.data.config.addressLines.join('\n'));
    setSaved(JSON.stringify(state.data.config));
    setErrors(state.data.errors);
  }, [state.data, cfg]);

  // Object URLs are per-document; drop the old one whenever we make a new one.
  const swapUrl = (next: string | null) => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = next;
    setPreviewUrl(next);
  };
  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const payload = (): LegalConfig | null =>
    cfg ? { ...cfg, addressLines: addressText.split('\n').map((l) => l.trim()).filter(Boolean) } : null;
  const dirty = !!cfg && JSON.stringify(payload()) !== saved;
  const canRender = !!saved && errors.length === 0 && !dirty;

  // Preview always reflects the saved config — the same thing the download renders.
  useEffect(() => {
    if (!canRender) return;
    let cancelled = false;
    setPreviewBusy(true);
    setPreviewErr(null);
    (async () => {
      try {
        const url = await apiBlobUrl(legalDocPath(client.orgId, tab));
        if (cancelled) { URL.revokeObjectURL(url); return; }
        if (tab === 'notices') {
          setNoticesText(await (await fetch(url)).text());
          URL.revokeObjectURL(url);
          swapUrl(null);
        } else {
          swapUrl(url);
        }
      } catch (e) {
        if (!cancelled) setPreviewErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setPreviewBusy(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, saved, canRender, client.orgId]);

  const set = <K extends keyof LegalConfig>(k: K, v: LegalConfig[K]) => setCfg((p) => (p ? { ...p, [k]: v } : p));

  const save = async () => {
    const body = payload();
    if (!body) return;
    setSaving(true);
    setSaveErr(null);
    try {
      const res = await saveLegalConfig(client.orgId, body);
      setCfg(res.config);
      setAddressText(res.config.addressLines.join('\n'));
      setSaved(JSON.stringify(res.config));
      setErrors(res.errors);
      qc.invalidateQueries({ queryKey: ['admin', 'legal', client.orgId] });
    } catch (e) {
      setSaveErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const FILENAMES: Record<LegalDoc, string> = { privacy: 'privacy-policy.html', terms: 'terms-of-use.html', notices: 'notices.md' };
  const download = async (doc: LegalDoc, format: 'html' | 'mdx' = 'html') => {
    // The endpoint needs the Bearer token, so fetch to a blob and click a temp anchor.
    const url = await apiBlobUrl(legalDocPath(client.orgId, doc, { download: true, format, fragment: format === 'html' && doc !== 'notices' && fragment }));
    const a = document.createElement('a');
    a.href = url;
    a.download = format === 'mdx' ? FILENAMES[doc].replace(/\.html$/, '.mdx') : FILENAMES[doc];
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Commits both MDX files to the client's Astro repo via the same GitHub App
  // path articles use. Admin-initiated, one click, from the saved config only.
  const pushToSite = async () => {
    setPushing(true);
    setPushErr(null);
    setPushResult(null);
    try {
      const r = await pushLegalPages(client.orgId);
      setPushResult({ repo: r.repo, branch: r.branch, files: r.files });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // apiFetch surfaces the JSON body after the status; pull the human message out if present.
      const m = msg.match(/"message":"([^"]+)"/) || msg.match(/"detail":"([^"]+)"/);
      setPushErr(m ? m[1] : msg);
    } finally {
      setPushing(false);
    }
  };

  const busy = saving || pushing;

  /* The page refuses a tab switch while this is dirty; the tab unmounts, so this
     is the only thing standing between an edit and silent loss. */
  useEffect(() => {
    onDirtyChange?.('legal', dirty);
    return () => onDirtyChange?.('legal', false);
  }, [dirty, onDirtyChange]);

  return (
    <div className="admin-form">
        <p className="dash-muted admin-form__lede">Privacy Policy, Terms of Use, and the notices for their forms and phone lines. Fill the form, save, preview, download — then drop the files into the client's site at <code>/privacy-policy</code> and <code>/terms-of-use</code>.</p>
        <div className="admin-form__body">
          {state.isLoading || !cfg ? (
            <p className="dash-muted">Loading…</p>
          ) : state.isError ? (
            <p className="admin-modal__err">Couldn't load: {state.error instanceof Error ? state.error.message : 'error'}</p>
          ) : (
            <div className="admin-legal">
              <div className="admin-legal__form">
                <section className="admin-sect">
                  <h4 className="admin-sect__title">Business</h4>
                  <div className="admin-prof">
                    <Input label="Legal business name" hint="Exactly as registered — appears in the policy header and contact block." value={cfg.clientLegalName} onChange={(e) => set('clientLegalName', e.target.value)} />
                    <div className="admin-prof__row">
                      <Input label="Short name" hint="Used in the phone announcement and release." value={cfg.clientShortName} onChange={(e) => set('clientShortName', e.target.value)} />
                      <Input label="Entity type" placeholder="a Minnesota limited liability company" value={cfg.entityType} onChange={(e) => set('entityType', e.target.value)} />
                    </div>
                    <div className="admin-prof__row">
                      <Input label="Type of business" placeholder="residential plumbing contractor" value={cfg.businessType} onChange={(e) => set('businessType', e.target.value)} />
                      <Input label="Service area" placeholder="the Twin Cities metro area" value={cfg.serviceArea} onChange={(e) => set('serviceArea', e.target.value)} />
                    </div>
                    <Input label="License number" hint="Optional — shown in the Terms if present." value={cfg.licenseNumber} onChange={(e) => set('licenseNumber', e.target.value)} />
                  </div>
                </section>

                <section className="admin-sect">
                  <h4 className="admin-sect__title">Contact &amp; jurisdiction</h4>
                  <div className="admin-prof">
                    <div className="admin-prof__row">
                      <Input label="Privacy contact email" type="email" hint="Where privacy requests arrive. The client's, not Triton's." value={cfg.email} onChange={(e) => set('email', e.target.value)} />
                      <Input label="Phone" value={cfg.phone} onChange={(e) => set('phone', e.target.value)} />
                    </div>
                    <Textarea label="Mailing address" hint="One line per row. Required by California for the contact section." rows={2} value={addressText} onChange={(e) => setAddressText(e.target.value)} />
                    <div className="admin-prof__row">
                      <Input label="State" hint="Governs the Terms — the client's state, not Minnesota unless they are here." placeholder="Minnesota" value={cfg.state} onChange={(e) => set('state', e.target.value)} />
                      <Input label="County" hint="Venue for disputes." placeholder="Hennepin County" value={cfg.county} onChange={(e) => set('county', e.target.value)} />
                    </div>
                    <div className="admin-prof__row">
                      <Input label="Effective date" placeholder="September 15, 2026" value={cfg.effectiveDate} onChange={(e) => set('effectiveDate', e.target.value)} />
                      <Input label="Last updated" placeholder="September 15, 2026" value={cfg.lastUpdated} onChange={(e) => set('lastUpdated', e.target.value)} />
                    </div>
                  </div>
                </section>

                <section className="admin-sect">
                  <h4 className="admin-sect__title">What the site actually uses</h4>
                  <p className="dash-muted admin-sect__note">Each switch adds or removes the matching disclosure. Only turn one on if it is really on the site.</p>
                  <div className="admin-prof">
                    <Input label="Call-tracking vendor" placeholder="Nimbata" value={cfg.callTrackingVendor} onChange={(e) => set('callTrackingVendor', e.target.value)} />
                    <div className="admin-legal__toggles">
                      <Switch label="Live chat on the site" checked={cfg.chat} onChange={(e) => set('chat', e.target.checked)} />
                      {cfg.chat && (
                        <div className="admin-prof__row">
                          <Input label="Chat vendor" placeholder="LiveChat" value={cfg.chatVendor} onChange={(e) => set('chatVendor', e.target.value)} />
                          <Input label="Chat transcript retention" placeholder="90 days" value={cfg.chatRetention} onChange={(e) => set('chatRetention', e.target.value)} />
                        </div>
                      )}
                      <Switch label="Third-party form vendor (Wufoo / Formspree)" checked={cfg.formVendorUsed} onChange={(e) => set('formVendorUsed', e.target.checked)} />
                      {cfg.formVendorUsed && <Input label="Form vendor" placeholder="Wufoo" value={cfg.formVendor} onChange={(e) => set('formVendor', e.target.value)} />}
                      <Switch label="Client texts leads (adds the TCPA consent checkbox)" checked={cfg.texting} onChange={(e) => set('texting', e.target.checked)} />
                      <Switch label="Careers / job application page" checked={cfg.careersPage} onChange={(e) => set('careersPage', e.target.checked)} />
                      {cfg.careersPage && <Input label="Job application retention" placeholder="2 years from application" value={cfg.jobApplicationRetention} onChange={(e) => set('jobApplicationRetention', e.target.value)} />}
                    </div>
                  </div>
                </section>

                <section className="admin-sect">
                  <h4 className="admin-sect__title">Advertising tags</h4>
                  <p className="dash-muted admin-sect__note">Hashed-data sharing (Enhanced Conversions, Meta CAPI) adds the §4.2 disclosure and the opt-out right. Check the site's tag setup before enabling.</p>
                  <div className="admin-legal__toggles">
                    <Switch label="Google Enhanced Conversions" checked={cfg.enhancedConversions} onChange={(e) => set('enhancedConversions', e.target.checked)} />
                    <Switch label="Meta Pixel" checked={cfg.metaPixel} onChange={(e) => set('metaPixel', e.target.checked)} />
                    <Switch label="Meta Conversions API" checked={cfg.metaCapi} onChange={(e) => set('metaCapi', e.target.checked)} />
                    <Switch label="Client is a CCPA-covered business (adds the Do Not Sell or Share footer link)" checked={cfg.ccpaCovered} onChange={(e) => set('ccpaCovered', e.target.checked)} />
                  </div>
                </section>

                <section className="admin-sect">
                  <h4 className="admin-sect__title">Retention &amp; verification</h4>
                  <div className="admin-prof">
                    <Input label="CRM retention" hint={'Type "indefinite" for the truthful default, or a period like "3 years after your last contact with us".'} value={cfg.retention} onChange={(e) => set('retention', e.target.value)} />
                    <div className="admin-prof__row">
                      <Input label="Call recording retention" hint="From the call-tracking plan settings." placeholder="12 months" value={cfg.callRecordingRetention} onChange={(e) => set('callRecordingRetention', e.target.value)} />
                      <Input label="Analytics retention" hint="GA4 data retention setting." placeholder="14 months" value={cfg.analyticsRetention} onChange={(e) => set('analyticsRetention', e.target.value)} />
                    </div>
                    <div className="admin-legal__toggles">
                      <Switch label="Verified: no session-replay / heat-mapping tools on the site" checked={cfg.noSessionReplayVerified} onChange={(e) => set('noSessionReplayVerified', e.target.checked)} />
                    </div>
                    <Input label="AI vendor named in the policy" value={cfg.aiVendorName} onChange={(e) => set('aiVendorName', e.target.value)} />
                  </div>
                </section>

                {errors.length > 0 && (
                  <ul className="admin-legal__errs">
                    {errors.map((e) => <li key={e}>{e}</li>)}
                  </ul>
                )}
              </div>

              <div className="admin-legal__preview">
                <div className="admin-legal__tabs" role="tablist">
                  {(['privacy', 'terms', 'notices'] as LegalDoc[]).map((d) => (
                    <button key={d} type="button" role="tab" aria-selected={tab === d} className={tab === d ? 'is-active' : ''} onClick={() => setTab(d)}>
                      {d === 'privacy' ? 'Privacy Policy' : d === 'terms' ? 'Terms of Use' : 'Notices'}
                    </button>
                  ))}
                </div>

                {!canRender ? (
                  <div className="admin-legal__empty">
                    {errors.length ? 'Complete the fields listed under the form, then save to preview.' : dirty ? 'Save to refresh the preview.' : 'Save once to render a preview.'}
                  </div>
                ) : previewErr ? (
                  <div className="admin-legal__empty">Preview failed: {previewErr}</div>
                ) : tab === 'notices' ? (
                  <pre className="admin-legal__pre">{previewBusy ? 'Rendering…' : noticesText}</pre>
                ) : previewUrl ? (
                  <iframe className="admin-legal__frame" title={`${tab} preview`} src={previewUrl} sandbox="" />
                ) : (
                  <div className="admin-legal__empty">Rendering…</div>
                )}

                <div className="admin-legal__downloads">
                  <Button variant="primary" size="sm" disabled={!canRender || pushing} onClick={pushToSite} iconLeft={<Icon name="upload" size={14} />}>
                    {pushing ? 'Pushing…' : 'Push to Astro site'}
                  </Button>
                  <Button variant="outline" size="sm" disabled={!canRender} onClick={() => download('notices')} iconLeft={<Icon name="download" size={14} />}>notices.md</Button>
                </div>
                {pushResult && (
                  <p className="dash-muted" style={{ fontSize: 13, margin: 0 }}>
                    Committed to <strong>{pushResult.repo}</strong> ({pushResult.branch}):{' '}
                    {pushResult.files.map((f, i) => (
                      <span key={f.path}>{i > 0 && ' · '}<a href={f.url} target="_blank" rel="noopener noreferrer">{f.path.split('/').pop()}</a></span>
                    ))}
                    . The site rebuilds on its next deploy.
                  </p>
                )}
                {pushErr && <p className="admin-modal__err" style={{ margin: 0 }}>{pushErr}</p>}
                <details>
                  <summary className="dash-muted" style={{ cursor: 'pointer', fontSize: 13 }}>Other formats (DNN / manual)</summary>
                  <div className="admin-legal__downloads" style={{ marginTop: 8 }}>
                    <Button variant="outline" size="sm" disabled={!canRender} onClick={() => download('privacy', 'mdx')} iconLeft={<Icon name="download" size={14} />}>privacy-policy.mdx</Button>
                    <Button variant="outline" size="sm" disabled={!canRender} onClick={() => download('terms', 'mdx')} iconLeft={<Icon name="download" size={14} />}>terms-of-use.mdx</Button>
                    <Button variant="outline" size="sm" disabled={!canRender} onClick={() => download('privacy')} iconLeft={<Icon name="download" size={14} />}>privacy-policy.html</Button>
                    <Button variant="outline" size="sm" disabled={!canRender} onClick={() => download('terms')} iconLeft={<Icon name="download" size={14} />}>terms-of-use.html</Button>
                  </div>
                  <Switch label="HTML as fragment (style + article only — for DNN HTML modules)" checked={fragment} onChange={(e) => setFragment(e.target.checked)} />
                </details>
                {state.data?.stored && state.data.updatedAt && (
                  <p className="dash-muted" style={{ fontSize: 12, margin: 0 }}>Last saved {new Date(state.data.updatedAt.replace(' ', 'T') + 'Z').toLocaleString()}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="admin-form__foot">
          {saveErr && <span className="admin-modal__err">{saveErr}</span>}
          {!saveErr && dirty && <span className="dash-muted admin-modal__hint">Unsaved changes</span>}
          <Button variant="primary" size="sm" onClick={save} disabled={busy || !dirty}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
    </div>
  );
}
